import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './Header';
import Navigation from './Navigation';
import FolderContent from './FolderContent';
import LoginScreen from './LoginScreen';
import Footer from './Footer';
import ThemeToggle from './ThemeToggle';
import { courseData } from '../data/courseData';
import { useTheme } from '../contexts/ThemeContext';

const VideocorsoFormativo = () => {
  const [activeFolder, setActiveFolder] = useState('folder1');
  const [completedLessons, setCompletedLessons] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('course_authenticated');
    if (isLoggedIn === 'true') {
      setIsAuthenticated(true);
      setCompletedLessons([]);
      localStorage.setItem('completedLessons', JSON.stringify([]));
    }
  }, []);

  const handleLogin = (password) => {
    if (password === 'aOxN#4WmHj|bt@B') {
      setIsAuthenticated(true);
      localStorage.setItem('course_authenticated', 'true');
      setCompletedLessons([]);
      localStorage.setItem('completedLessons', JSON.stringify([]));
      return true;
    }
    return false;
  };

  const toggleComplete = (lessonId) => {
    setCompletedLessons(prev => {
      const newCompleted = prev.includes(lessonId) 
        ? prev.filter(id => id !== lessonId)
        : [...prev, lessonId];
      localStorage.setItem('completedLessons', JSON.stringify(newCompleted));
      return newCompleted;
    });
  };

  const handleNextChapter = async (nextFolderId) => {
    setIsTransitioning(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    setActiveFolder(nextFolderId);
    setIsTransitioning(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getTotalLessons = () => {
    return courseData.reduce((total, folder) => {
      return total + folder.subfolders.reduce((subTotal, subfolder) => {
        return subTotal + subfolder.lessons.filter(l => l.type !== 'text').length;
      }, 0);
    }, 0);
  };

  const getProgressPercentage = () => {
    const total = getTotalLessons();
    if (total === 0) return 0;
    const percentage = Math.round((completedLessons.length / total) * 100);
    return Math.min(percentage, 100);
  };

  const isFolderUnlocked = (folderIndex) => {
    if (folderIndex === 0) return true;
    
    const prevFolder = courseData[folderIndex - 1];
    const prevFolderLessons = prevFolder.subfolders.reduce((acc, subfolder) => {
      return [...acc, ...subfolder.lessons.filter(l => l.type !== 'text').map(lesson => lesson.id)];
    }, []);
    
    return prevFolderLessons.every(lessonId => completedLessons.includes(lessonId));
  };

  const isSubfolderUnlocked = (folderIndex, subfolderIndex) => {
    if (subfolderIndex === 0) return true;
    
    const currentFolder = courseData[folderIndex];
    const prevSubfolder = currentFolder.subfolders[subfolderIndex - 1];
    const prevLessons = prevSubfolder.lessons.filter(l => l.type !== 'text').map(lesson => lesson.id);
    
    return prevLessons.every(lessonId => completedLessons.includes(lessonId));
  };

  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 to-white'}`}>
      <div className="max-w-6xl mx-auto p-5">
        <ThemeToggle />
        <Header 
          completedCount={completedLessons.length} 
          totalCount={getTotalLessons()} 
          progressPercentage={getProgressPercentage()} 
        />
        
        <Navigation 
          activeFolder={activeFolder} 
          setActiveFolder={setActiveFolder} 
          courseData={courseData}
          isFolderUnlocked={isFolderUnlocked}
        />

        <motion.div 
          className={`rounded-custom p-6 shadow-custom-xl border transition-colors duration-300 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <AnimatePresence mode="wait">
            {!isTransitioning && courseData.map((folder, folderIndex) => (
              activeFolder === folder.id && (
                <FolderContent 
                  key={folder.id}
                  folder={folder} 
                  folderIndex={folderIndex}
                  completedLessons={completedLessons}
                  toggleComplete={toggleComplete}
                  isSubfolderUnlocked={isSubfolderUnlocked}
                  onNextChapter={handleNextChapter}
                  courseData={courseData}
                />
              )
            ))}
          </AnimatePresence>
        </motion.div>
        <Footer />
      </div>
    </div>
  );
};

export default VideocorsoFormativo;